import ReactFlow, {
  Background,
  Controls,
  Edge,
  Node,
  NodeTypes,
  ReactFlowInstance,
  ReactFlowProvider,
} from "reactflow";
import { AutoExpandTablesForRefs } from "./AutoExpandTablesForRefs";
import {
  createRefEdge,
  createTableNode,
  DEFAULT_MIN_ZOOM,
  dedupeAndFilterRefs,
  isRefEdge,
  toggleColumnEdges,
  toggleModelEdges,
  getCurrentTableId,
  handleTableNavigation,
} from "../utils";
import LineageLegend from "./LineageLegend";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import {
  ColumnNode,
  SeeMoreNode,
  SelfConnectingEdge,
  TableNode,
  DashboardNode,
} from "./CustomNodes";
import { useLineageContext, useLineageDispatch } from "../context/LineageProvider";
import { SidebarModal } from "./sidebarModal/SidebarModal";
import {
  setAiEnabled,
  setCollectColumns,
  setConfig,
  setDefaultExpansion,
  setEnabledRefSources,
  setIncludeSourcesInInference,
  setInferenceConfidenceThreshold,
  setLightdashEnabled,
  setMinRange,
  setMoreTables,
  setNodeCount,
  setNonSelectCheck,
  setRefs,
  setSelectCheck,
  setSelectedColumn,
  setSelectedTable,
  setShowCodeModal,
  setShowRefs,
  setSidebarScreen,
} from "../context/LineageSlice";
import { MoreTables } from "./MoreTables";
import {
  TABLES_SIDEBAR,
  COLUMNS_SIDEBAR,
  EXPOSURE_SIDEBAR,
  FEEDBACK_SIDEBAR,
  SETTINGS_SIDEBAR,
  SNOWFLAKE_STAGE_SIDEBAR,
  DASHBOARD_SIDEBAR,
  FUNCTION_SIDEBAR,
} from "../constants";
import { TableDetails } from "./TableDetails";
import ExposureDetails from "./ExposureDetails";
import DashboardDetails from "./DashboardDetails";
import { Feedback } from "./Feedback";
import { Settings } from "./Settings";
import { getLineageSettings, getRelationships, initLineage } from "../api";
import "./index.scss";
import { PopoverContext } from "./BetterPopover";
import ExpandWithConfidenceCard from "./ExpandWithConfidenceCard";
import { RefEdge, REF_EDGE_TYPE } from "./RefEdge";
import { RefEdgeMarkers } from "./RefEdgeMarkers";
import RefsToggleButton from "./RefsToggleButton";
import SettingsButton from "./SettingsButton";
import ResetButton from "./ResetButton";
import { Table } from "../types";
import {
  applyTableExpansion,
  calculateMinLevel,
  calculateNodeCount,
  expandTableLineageLevelWise,
  highlightTableConnections,
  layoutElementsOnCanvas,
} from "../graph";
import { LineageModal } from "./LineageModals";
import ExportButton from "./ExportButton";
import SnowflakeStageDetails from "./SnowflakeStageDetails";
import FunctionDetails from "./FunctionDetails";
import SidebarErrorBoundary from "./SidebarErrorBoundary";

const nodeTypes: NodeTypes = {
  table: TableNode,
  seeMore: SeeMoreNode,
  dashboard: DashboardNode,
  column: ColumnNode,
};
const edgeTypes = { selfConnecting: SelfConnectingEdge, [REF_EDGE_TYPE]: RefEdge };

const DynamicLineage = ({
  flow,
  theme,
}: {
  flow: MutableRefObject<ReactFlowInstance<unknown, unknown> | undefined>;
  theme: "dark" | "light";
}) => {
  const [openedPopover, setOpenedPopover] = useState("");
  const {
    state: {
      selectCheck,
      nonSelectCheck,
      sidebarScreen,
      leftExpansion,
      rightExpansion,
      selectedColumn,
      defaultExpansion,
      errors,
      refs,
      showRefs,
      enabledRefSources,
      inferenceConfidenceThreshold,
      expandedTables,
      collectColumns,
      aiEnabled,
    },
    rerender,
  } = useLineageContext();
  const lineageDispatch = useLineageDispatch();
  // hack for default expansion
  const defaultExpansionRef = useRef(defaultExpansion);

  const onCllCancelled = useCallback(() => {
    if (flow.current) {
      const edges = flow.current.getEdges();
      toggleModelEdges(edges, true);
      toggleColumnEdges(edges, false);
      flow.current.setEdges(edges);
    }
  }, [flow]);

  // ERD overlay: refs / filter state are mirrored into mutable refs so that
  // `reconcileRefEdges` can be called from `renderStartNode` without
  // churning the callback's deps (renderStartNode re-creation resubscribes
  // the document event listener).
  const refsRef = useRef(refs);
  const showRefsRef = useRef(showRefs);
  const enabledRefSourcesRef = useRef(enabledRefSources);
  const thresholdRef = useRef(inferenceConfidenceThreshold);
  useEffect(() => {
    refsRef.current = refs;
  }, [refs]);
  useEffect(() => {
    showRefsRef.current = showRefs;
  }, [showRefs]);
  useEffect(() => {
    enabledRefSourcesRef.current = enabledRefSources;
  }, [enabledRefSources]);
  useEffect(() => {
    thresholdRef.current = inferenceConfidenceThreshold;
  }, [inferenceConfidenceThreshold]);

  const reconcileRefEdges = useCallback(() => {
    const _flow = flow.current;
    if (!_flow) return;
    const currentEdges = _flow.getEdges().filter(e => !isRefEdge(e));
    if (!showRefsRef.current) {
      _flow.setEdges(currentEdges);
      return;
    }
    const nodeIds = new Set(_flow.getNodes().map(n => n.id));
    // Dedup before edge creation so multiple sources agreeing on the same
    // FK render as one line. The dedupe helper picks a winner per source
    // priority and tags every visible edge with the full
    // `corroboratingSources` list — used by `RefEdge` to render the
    // 4-dot indicator at the midpoint.
    const refEdges = dedupeAndFilterRefs(
      Object.values(refsRef.current),
      enabledRefSourcesRef.current,
      thresholdRef.current
    )
      .map(({ ref, corroboratingSources }) => createRefEdge(ref, nodeIds, corroboratingSources))
      .filter((e): e is NonNullable<typeof e> => e !== undefined);
    _flow.setEdges([...currentEdges, ...refEdges]);
  }, [flow]);

  useEffect(() => {
    reconcileRefEdges();
  }, [refs, showRefs, enabledRefSources, inferenceConfidenceThreshold, reconcileRefEdges]);

  // Sync `expandedTables` preference to the canvas: add column nodes for
  // tables marked expanded that don't have rows yet, remove columns for
  // tables no longer expanded (preserving CLL-added columns). Re-runs ref
  // edge reconciliation afterward so refs reanchor on column nodes when
  // available.
  useEffect(() => {
    const _flow = flow.current;
    if (!_flow) return;
    const tableMetadata = new Map<string, { columns: string[]; nodeType: string }>();
    for (const n of _flow.getNodes()) {
      if (n.type !== "table") continue;
      const cols = (n.data as { columns?: Record<string, unknown> })?.columns ?? {};
      tableMetadata.set(n.id, {
        columns: Object.keys(cols),
        nodeType: (n.data as { nodeType?: string })?.nodeType ?? "model",
      });
    }
    const [next, nextEdges] = applyTableExpansion(
      _flow.getNodes(),
      _flow.getEdges(),
      expandedTables,
      collectColumns,
      tableMetadata
    );
    _flow.setNodes(next);
    _flow.setEdges(nextEdges);
    reconcileRefEdges();
  }, [expandedTables, collectColumns, flow, reconcileRefEdges]);

  const renderStartNode = useCallback(
    async (args: {
      node?: Table;
      aiEnabled: boolean;
      lightdashEnabled: boolean;
      showCodeModal: boolean;
      config: { exportFinalLineage: boolean };
    }) => {
      lineageDispatch(setSidebarScreen(""));
      if (!args) return;
      lineageDispatch(setAiEnabled(args.aiEnabled));
      lineageDispatch(setLightdashEnabled(args.lightdashEnabled));
      lineageDispatch(setShowCodeModal(args.showCodeModal));
      lineageDispatch(setConfig(args.config));
      const { node } = args;
      const _flow = flow.current;
      if (!_flow || !node) return;
      const existingNode = _flow.getNode(node.table);
      if (existingNode) {
        lineageDispatch(setSelectedTable(node.table));
        let nodes = _flow.getNodes();
        let edges = _flow.getEdges();
        if (!selectedColumn?.name) {
          [nodes, edges] = highlightTableConnections(nodes, edges, node.table);
          _flow.setNodes(nodes);
          _flow.setEdges(edges);
        }
        lineageDispatch(setMinRange(calculateMinLevel(nodes, edges, node.table)));
        lineageDispatch(
          setNodeCount(
            await calculateNodeCount(nodes, edges, node.table, leftExpansion, rightExpansion)
          )
        );
        return;
      }
      let nodes: Node[] = [];
      let edges: Edge[] = [];
      nodes = [createTableNode(node, 0, "")];
      [nodes, edges] = await expandTableLineageLevelWise(
        nodes,
        edges,
        node.table,
        -defaultExpansionRef.current,
        defaultExpansionRef.current
      );
      lineageDispatch(setSelectedTable(node.table));
      lineageDispatch(setSelectedColumn({ table: "", name: "" }));
      lineageDispatch(setCollectColumns({}));
      lineageDispatch(setMoreTables({}));
      [nodes, edges] = highlightTableConnections(nodes, edges, node.table);
      layoutElementsOnCanvas(nodes, edges);
      _flow.setNodes(nodes);
      _flow.setEdges(edges);
      _flow.fitView({ minZoom: DEFAULT_MIN_ZOOM, duration: 500 });
      lineageDispatch(setMinRange(calculateMinLevel(nodes, edges, node.table)));
      lineageDispatch(
        setNodeCount(
          await calculateNodeCount(nodes, edges, node.table, leftExpansion, rightExpansion)
        )
      );
      reconcileRefEdges();
      rerender();
    },
    [
      lineageDispatch,
      flow,
      leftExpansion,
      rightExpansion,
      rerender,
      selectedColumn?.name,
      reconcileRefEdges,
    ]
  );

  useEffect(() => {
    document.addEventListener("cll_cancelled", onCllCancelled);
    const handleRenderStartNode = (e: CustomEvent) => {
      console.log("renderStartNode", e.detail);
      renderStartNode(e.detail);
    };
    // @ts-ignore
    document.addEventListener("renderStartNode", handleRenderStartNode);

    return () => {
      document.removeEventListener("cll_cancelled", onCllCancelled);
      // @ts-ignore
      document.removeEventListener("renderStartNode", handleRenderStartNode);
    };
  }, [onCllCancelled, renderStartNode]);

  const applySettings = useCallback(async () => {
    const settings = await getLineageSettings();
    lineageDispatch(setSelectCheck(settings.showSelectEdges));
    lineageDispatch(setNonSelectCheck(settings.showNonSelectEdges));
    lineageDispatch(setDefaultExpansion(settings.defaultExpansion));
    defaultExpansionRef.current = settings.defaultExpansion;
    // ERD overlay state — apply only when persisted to avoid clobbering
    // the slice's defaults on a fresh project.
    if (typeof settings.showRefs === "boolean") {
      lineageDispatch(setShowRefs(settings.showRefs));
    }
    if (settings.enabledRefSources) {
      lineageDispatch(
        setEnabledRefSources({
          test: settings.enabledRefSources.test ?? true,
          contract: settings.enabledRefSources.contract ?? true,
          semantic: settings.enabledRefSources.semantic ?? true,
          inferred: settings.enabledRefSources.inferred ?? false,
        })
      );
    }
    if (typeof settings.inferenceConfidenceThreshold === "number") {
      lineageDispatch(setInferenceConfidenceThreshold(settings.inferenceConfidenceThreshold));
    }
    if (typeof settings.includeSourcesInInference === "boolean") {
      lineageDispatch(setIncludeSourcesInInference(settings.includeSourcesInInference));
    }
  }, [lineageDispatch]);

  useEffect(() => {
    applySettings();
  }, [applySettings]);

  // ERD overlay: fetch declared/inferred refs once per panel open.
  // Gated on `aiEnabled` (Altimate API key) — same gate as other premium
  // lineage features. Backend mirrors this guard so a stale call from an
  // older webview against a newer extension still returns empty.
  const applyRefs = useCallback(async () => {
    if (!aiEnabled) {
      lineageDispatch(setRefs([]));
      return;
    }
    try {
      const response = await getRelationships();
      lineageDispatch(setRefs(response.refs ?? []));
    } catch (error) {
      // Best-effort fetch — overlay simply renders no edges if the backend
      // doesn't understand the command yet.
      console.warn("getRelationships failed", error);
      lineageDispatch(setRefs([]));
    }
  }, [lineageDispatch, aiEnabled]);

  useEffect(() => {
    applyRefs();
  }, [applyRefs]);

  useEffect(() => {
    const _flow = flow.current;
    if (!_flow) return;
    const handleEdgeVisibilityChanges = async () => {
      const _column = selectedColumn;
      if (!_column?.name) return;
      const _edges = _flow.getEdges();
      if ((selectCheck && nonSelectCheck) || (!selectCheck && !nonSelectCheck)) {
        for (const e of _edges) e.hidden = false;
        _flow.setEdges(_edges);
        return;
      }
      for (const e of _edges) {
        e.hidden = false;
        const _type = (e.data as { type: string })?.type;
        if (!_type) continue;
        if (_type === "direct") e.hidden = !selectCheck;
        if (_type === "indirect") e.hidden = !nonSelectCheck;
      }
      _flow.setEdges(_edges);
    };
    handleEdgeVisibilityChanges();
  }, [selectCheck, nonSelectCheck, flow, selectedColumn, lineageDispatch, errors]);

  const afterFlowRender = (flowInstance: ReactFlowInstance) => {
    flow.current = flowInstance;
    initLineage();
  };

  return (
    <div
      className="lineage_flow_component"
      style={{ width: "100%", height: "100%" }}
      data-theme={theme}
    >
      <PopoverContext.Provider value={{ openedPopover, setOpenedPopover }}>
        <RefEdgeMarkers />
        <ReactFlowProvider>
          <AutoExpandTablesForRefs />
          <ReactFlow
            defaultNodes={[]}
            defaultEdges={[]}
            onInit={_flow => afterFlowRender(_flow)}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            style={{ background: "var(--bg-color)" }}
            proOptions={{ hideAttribution: true }}
            minZoom={DEFAULT_MIN_ZOOM}
          >
            <Background />
            <Controls />
          </ReactFlow>
          <LineageLegend />
          <SidebarModal
            isOpen={sidebarScreen !== ""}
            closeModal={() => lineageDispatch(setSidebarScreen(""))}
            width="30vw"
          >
            <SidebarErrorBoundary key={sidebarScreen}>
              {sidebarScreen === TABLES_SIDEBAR && <MoreTables />}
              {sidebarScreen === COLUMNS_SIDEBAR && (
                <TableDetails
                  lineageType="dynamic"
                  currentTableId={getCurrentTableId()}
                  onNavigate={handleTableNavigation}
                />
              )}
              {sidebarScreen === EXPOSURE_SIDEBAR && <ExposureDetails />}
              {sidebarScreen === DASHBOARD_SIDEBAR && (
                <DashboardDetails onNavigate={handleTableNavigation} />
              )}
              {sidebarScreen === SNOWFLAKE_STAGE_SIDEBAR && <SnowflakeStageDetails />}
              {sidebarScreen === FUNCTION_SIDEBAR && <FunctionDetails />}
              {sidebarScreen === FEEDBACK_SIDEBAR && (
                <Feedback close={() => setSidebarScreen("")} />
              )}
              {sidebarScreen === SETTINGS_SIDEBAR && (
                <Settings applyDefault={(v: number) => (defaultExpansionRef.current = v)} />
              )}
            </SidebarErrorBoundary>
          </SidebarModal>
          {flow.current ? <ExportButton flow={flow.current} /> : null}
          <ExpandWithConfidenceCard />
          <RefsToggleButton />
          <SettingsButton />
          {flow.current ? <ResetButton flow={flow.current} /> : null}
          <LineageModal />
        </ReactFlowProvider>
      </PopoverContext.Provider>
    </div>
  );
};

export default DynamicLineage;
